import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetTitle, SheetClose } from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { X } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { patientService } from '@/services/patientService';
import { VALID_TLDS_SET } from '@/lib/email';

const sendPatientFormLinkSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .refine(
      (email) => {
        const parts = email.split('@');
        if (parts.length !== 2) return false;
        const domain = parts[1];
        const tld = domain.split('.').pop()?.toLowerCase() ?? '';
        return VALID_TLDS_SET.has(tld);
      },
      'Please enter a valid email address with a real domain (e.g. .com, .org)'
    ),
});

type SendPatientFormLinkFormData = z.infer<typeof sendPatientFormLinkSchema>;

interface SendPatientFormLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicId: string;
  practitionerId?: string;
  isPractitioner?: boolean;
  practitioners?: { value: string; label: string }[];
  /** When true (guided clinic), default to practitioners[0] so the dropdown shows an active practitioner after MDI */
  isGuided?: boolean;
  onSuccess?: () => void;
}

const SendPatientFormLinkDialog: React.FC<SendPatientFormLinkDialogProps> = ({
  open,
  onOpenChange,
  clinicId,
  practitionerId,
  isPractitioner = true,
  practitioners = [],
  isGuided = false,
  onSuccess,
}) => {
  const [selectedPractitioner, setSelectedPractitioner] = useState('');
  const [sending, setSending] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SendPatientFormLinkFormData>({
    resolver: zodResolver(sendPatientFormLinkSchema),
    defaultValues: { email: '' },
  });

  useEffect(() => {
    if (open) {
      if (isPractitioner && practitionerId) {
        setSelectedPractitioner(practitionerId);
      } else if (isGuided && practitioners.length > 0) {
        setSelectedPractitioner(practitioners[0].value);
      } else {
        setSelectedPractitioner('');
      }
    }
  }, [open, isPractitioner, practitionerId, isGuided, practitioners]);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const hasPractitionerAssignment =
    isPractitioner && practitionerId
      ? true
      : isGuided
        ? practitioners.length > 0
        : !!selectedPractitioner;

  const onSubmit = async (data: SendPatientFormLinkFormData) => {
    const effectivePractitionerId = isPractitioner
      ? practitionerId
      : selectedPractitioner || (isGuided && practitioners.length > 0 ? practitioners[0].value : undefined);
    if (!isPractitioner && !effectivePractitionerId) {
      toast({
        title: 'Error',
        description: 'Please select a practitioner.',
        variant: 'destructive',
      });
      return;
    }

    setSending(true);
    try {
      const response = await patientService.generatePatientFormLink({
        email: data.email,
        clinic_id: clinicId,
        practitioner_id: effectivePractitionerId!,
      });

      if (response.success) {
        toast({
          title: 'Success',
          description: `Form link sent to ${data.email}`,
          variant: 'success',
        });
        onOpenChange(false);
        reset();
        setSelectedPractitioner('');
        onSuccess?.();
      } else {
        toast({
          title: 'Error',
          description: response.message || 'Failed to send form link',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error(error);
      toast({
        title: 'Error',
        description: error?.response?.data?.error?.message || error?.message || 'Failed to send form link',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='flex w-full max-w-md flex-col gap-0 rounded-l-2xl p-0 [&>button]:hidden'>
        {/* Header */}
        <div className='flex items-center justify-between border-b border-slate-200 px-6 py-4'>
          <SheetTitle className='font-semibold text-slate-900' style={{ fontSize: '1.05rem' }}>
            Send Patient Form Link
          </SheetTitle>
          <SheetClose asChild>
            <button className='flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-50'>
              <X className='h-5 w-5 text-slate-600' />
            </button>
          </SheetClose>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit(onSubmit)} className='flex flex-1 flex-col overflow-hidden'>
          <div className='flex-1 overflow-y-auto px-6 py-6'>
            <div className='space-y-4'>
              <p className='text-sm text-slate-600'>
                Send a secure form link to a patient's email address. They will be able to fill out
                their demographic and medical information.
              </p>

              {/* Practitioner Selection (only for non-practitioners; hidden for guided — we use practitioners[0]) */}
              {!isPractitioner && !isGuided && (
                <div className='space-y-2'>
                  <Label htmlFor='practitioner-select'>
                    Practitioner <span className='text-red-500'>*</span>
                  </Label>
                  <Select value={selectedPractitioner} onValueChange={setSelectedPractitioner}>
                    <SelectTrigger
                      id='practitioner-select'
                      className='h-10 w-full border-slate-200 transition-colors duration-200 focus:border-black focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0'
                    >
                      <SelectValue placeholder='Select a practitioner' />
                    </SelectTrigger>
                    <SelectContent>
                      {practitioners.map(practitioner => (
                        <SelectItem key={practitioner.value} value={practitioner.value}>
                          {practitioner.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className='text-xs text-slate-500'>
                    Select the practitioner who will be assigned to this patient.
                  </p>
                </div>
              )}

              {/* Email Input */}
              <div className='space-y-2'>
                <Label htmlFor='patient-email' className='required'>
                  Patient Email 
                </Label>
                <Input
                  id='patient-email'
                  type='email'
                  placeholder='patient@example.com'
                  autoComplete='new-password'
                  {...register('email')}
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className='text-xs text-red-500'>{errors.email.message}</p>
                )}
                <p className='text-xs text-slate-500'>
                  The link will be valid for 30 days and can only be used once.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
            <Button
              variant='ghost'
              type='button'
              className='font-medium text-slate-700 hover:bg-transparent hover:text-slate-900'
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              className='h-11 rounded-lg bg-brand-teal px-8 hover:bg-brand-teal/90'
              disabled={sending || !hasPractitionerAssignment}
            >
            {sending ? (
              <div className='flex items-center space-x-2'>
                <div className='h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent' />
                <span>Sending...</span>
              </div>
            ) : (
              <span>Send Link</span>
            )}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default SendPatientFormLinkDialog;

